import { getHoursWrapped } from "@/components/calendar_view/_utils"
import { type GetReservationPositionsArgs } from "@/store"

const MIN_COLUMN_WIDTH = 120
const MIN_COLUMN_HEIGHT = 50
const TIME_COLUMN_WIDTH = 70
const TABLE_GROUP_LABEL_HEIGHT = 30
const TABLE_LABEL_HEIGHT = 30

const getAllParentBottomMarginsAndPaddings = (element: HTMLElement): number => {
  let totalMarginAndPadding = 0
  let currentElement: HTMLElement | null = element

  while (currentElement) {
    const style = window.getComputedStyle(currentElement)
    const marginBottom = parseInt(style.marginBottom, 10)
    const paddingBottom = parseInt(style.paddingBottom, 10)

    totalMarginAndPadding += marginBottom + paddingBottom

    if (currentElement.tagName.toLowerCase() === "body") {
      break
    }

    currentElement = currentElement.parentElement
  }

  return totalMarginAndPadding
}

const calculateCalendarHeight = (container: HTMLElement): number => {
  const parent = container.parentElement
  const elementTop = container.getBoundingClientRect().top + window.scrollY
  const parentBottoms = getAllParentBottomMarginsAndPaddings(container)

  const siblingsHeight = Array.from(parent.children)
    .slice(Array.from(parent.children).indexOf(container) + 1)
    .reduce((acc, el) => acc + el.clientHeight, 0)

  return window.innerHeight - elementTop - siblingsHeight - parentBottoms
}

const calculateViewportDimensions = (container: HTMLElement) =>
  container
    ? {
        width: container.clientWidth,
        height: calculateCalendarHeight(container),
      }
    : {
        width: null,
        height: null,
      }

const calculateCanvasContainerDimensions = ({ viewport }) => {
  if (!viewport.width || !viewport.height) {
    return viewport
  }

  return {
    width: viewport.width - TIME_COLUMN_WIDTH,
    height: viewport.height,
  }
}

const calculateCanvasDimensions = ({ viewport, canvasContainer, timeline, columnWidth, tablesCount }) => {
  return {
    height: Math.max(viewport.height - TABLE_GROUP_LABEL_HEIGHT - TABLE_LABEL_HEIGHT, timeline.height),
    width: Math.max(columnWidth * tablesCount, canvasContainer.width),
  }
}

const getTablesFromSelectedTableGroupIds = ({ venue, selectedTableGroupIds }) =>
  venue.tables.filter((table) => !table.deleted && selectedTableGroupIds.includes(table.tableGroup.id))

const calculateColumnWidth = ({ canvasContainer, tablesCount }) => {
  return Math.max(MIN_COLUMN_WIDTH, canvasContainer.width / tablesCount)
}

const calculateTimeline = ({ canvasContainer, hours }) => {
  const padding = TABLE_GROUP_LABEL_HEIGHT + TABLE_LABEL_HEIGHT

  const containerTop = 0
  const containerHeight = canvasContainer.height
  const containerWidth = canvasContainer.width + TIME_COLUMN_WIDTH

  const top = TABLE_GROUP_LABEL_HEIGHT + TABLE_LABEL_HEIGHT
  const width = containerWidth

  const minHeight = hours.length * MIN_COLUMN_HEIGHT
  const height = Math.max(canvasContainer.height - containerTop - padding, minHeight)
  const offset = Math.min(height / hours.length, 300)

  return {
    timelineContainer: {
      width,
      height: containerHeight,
      top: containerTop,
    },
    timeline: {
      columnWidth: TIME_COLUMN_WIDTH,
      padding,
      width,
      height,
      top,
      offset,
    },
  }
}

const calculateHeadersDimensions = ({ canvas }) => {
  return {
    width: canvas.width,
    height: TABLE_GROUP_LABEL_HEIGHT + TABLE_LABEL_HEIGHT,
  }
}

export const calculateDimensions = ({ container, venue, selectedTableGroupIds, startHour, endHour }) => {
  const tables = getTablesFromSelectedTableGroupIds({
    venue,
    selectedTableGroupIds,
  })
  const tablesCount = tables.length
  const hours = getHoursWrapped(startHour, endHour)
  const viewport = calculateViewportDimensions(container)
  const canvasContainer = calculateCanvasContainerDimensions({ viewport })
  const columnWidth = calculateColumnWidth({
    canvasContainer,
    tablesCount,
  })
  const { timeline, timelineContainer } = calculateTimeline({
    canvasContainer,
    hours,
  })

  const canvas = calculateCanvasDimensions({
    viewport,
    canvasContainer,
    timeline,
    columnWidth,
    tablesCount,
  })

  const headers = calculateHeadersDimensions({ canvas })

  const getReservationPosition = ({ tableId, startTime, endTime }: GetReservationPositionsArgs) => {
    // TODO: take minutes into an account when calculating the height
    const tableIndex = tables.findIndex((table) => table.id === tableId)
    const startHour = startTime.hour
    const endHour = endTime.hour

    // The reservation is currently not visible
    if (tableIndex === -1) {
      return null
    }

    const startMinuteOffset = startTime.minute / 60
    const endMinuteOffset = endTime.minute / 60

    const top =
      (hours.findIndex((hour) => hour === startHour) + startMinuteOffset) * timeline.offset + timeline.offset / 2
    const height =
      (hours.findIndex((hour) => hour === endHour) + endMinuteOffset) * timeline.offset -
      top +
      timeline.offset / 2 +
      endMinuteOffset

    const left = tableIndex * columnWidth
    const width = columnWidth

    return {
      top,
      left,
      width,
      height,
    }
  }

  return {
    viewport,
    headers,
    canvasContainer,
    canvas,
    columnWidth,
    timelineContainer,
    timeline,
    tableGroupLabelHeight: TABLE_GROUP_LABEL_HEIGHT,
    tableLabelHeight: TABLE_LABEL_HEIGHT,

    getReservationPosition,
  }
}
